// La barre de recherche du CRM : la même dans toutes les pages.
//
// Un seul champ, toujours au même endroit (sous le titre), toujours avec la
// même promesse : on tape ce dont on se souvient, dans n'importe quel ordre.
// La croix vide la recherche d'un geste — sur un toit, on ne fait pas dix
// retours arrière au clavier.

import { Search, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface BarreRechercheProps {
  /** Le texte tapé. */
  valeur: string;
  onChange: (valeur: string) => void;
  /** Ce qu'on cherche ici : « un client », « un devis »… */
  quoi: string;
  /** Les champs réellement fouillés, annoncés au survol et aux lecteurs d'écran. */
  champs?: string;
  /** Nombre de lignes affichées / nombre total : le compteur de résultats. */
  resultats?: number;
  total?: number;
  className?: string;
}

const NOMBRE = new Intl.NumberFormat('fr-FR');

/**
 * « un client » → « 1 066 clients ».
 *
 * Tous les libellés du CRM suivent ce modèle (un client, une facture, un devis,
 * un véhicule…) : on retire l'article, et on met au pluriel sauf si le mot finit
 * déjà par s, x ou z. « 0 client » et « 1 client » restent au singulier.
 */
function compter(n: number, quoi: string): string {
  const nom = quoi.replace(/^une? /, '');
  const pluriel = n >= 2 && !/[sxz]$/.test(nom) ? `${nom}s` : nom;
  return `${NOMBRE.format(n)} ${pluriel}`;
}

export function BarreRecherche({
  valeur,
  onChange,
  quoi,
  champs,
  resultats,
  total,
  className,
}: BarreRechercheProps) {
  const libelle = `Rechercher ${quoi}${champs ? ` (${champs})` : ''}…`;
  const filtre = valeur.trim() !== '';
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <div className="relative w-full sm:max-w-md">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={valeur}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={libelle}
          aria-label={libelle}
          className="pl-9 pr-10"
        />
        {filtre ? (
          <button
            type="button"
            aria-label="Effacer la recherche"
            onClick={() => {
              onChange('');
            }}
            className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {filtre && resultats !== undefined ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {resultats === 0
            ? 'Aucun résultat'
            : `${NOMBRE.format(resultats)} résultat${resultats > 1 ? 's' : ''}${
                total === undefined ? '' : ` sur ${NOMBRE.format(total)}`
              }`}
        </p>
      ) : !filtre && total !== undefined ? (
        // Sans recherche, le total : c'est la première chose qu'on veut savoir
        // d'une liste, et la seule façon de voir qu'elle n'est pas tronquée.
        <p className="text-sm text-muted-foreground">{compter(total, quoi)}</p>
      ) : null}
    </div>
  );
}
